import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, Loader2, Trash2, Link2, UserPlus, Search, Users, 
  Flame, Thermometer, Snowflake, Activity, Network, Copy, 
  CheckCircle2, AlertCircle, Clock, TrendingUp, Play, XCircle, Zap,
  MessageSquare, Phone, Send, PhoneCall, PhoneIncoming, PhoneOutgoing, Edit2,
  Linkedin, Globe, Mail
} from 'lucide-react';
import type { 
  Contact, ContactType, ContactStatus, TeamMember, Team, ContactRevenue, RehubUser,
  Signal, Relationship, ClientMetrics, TaskTemplate, Enrolment
} from '../../types';
import type { InstantlyList, InstantlyCampaign } from '../../services/instantly';
import { CONTACT_TYPES, CONTACT_STATUSES, CONTACT_SOURCES, REFERRAL_TYPES, ICP_FITS, LEAD_SEGMENTS, CHANNEL_PREFERENCES } from '../../constants';
import { createContact, updateContact, deleteContact, linkContactToRehubUser, type CreateContactInput } from '../../services/contacts';
import { ctaHintForSegment, looksFabricatedEmail } from '../../services/outbound';
import type { IcpFit, LeadSegment, ChannelPreference } from '../../types';
import { getContactRevenue, searchRehubUsers, formatCurrency } from '../../services/billing';
import { 
  getSignalsForContact, getRelationshipsForContact, getClientMetrics, 
  createRelationship, deleteRelationship, setReferrer, searchContacts 
} from '../../services/intelligence';
import { getTaskTemplates, getEnrolmentsForContact, enrolContactInSequence, cancelEnrolment } from '../../services/sequences';
import { getUserSettings, generateBookingMessage } from '../../services/settings';
import { getLatestSummary, createSummary } from '../../services/summaries';
import { 
  pushContactToInstantly, getInstantlyLists, getInstantlyCampaigns, 
  getInstantlyOrgSettings, getInstantlyMapping 
} from '../../services/instantly';
import {
  getContactMessages, getContactCalls, getContactComms, saveContactComms,
  sendMessage, MESSAGE_TEMPLATES, applyTemplate,
} from '../../services/twilio';

interface LeadDrawerProps {
  contact: Contact | null;
  team: Team;
  members: TeamMember[];
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}

const LeadDrawer: React.FC<LeadDrawerProps> = ({ contact, team, members, userId, onClose, onSaved }) => {
  const isEdit = !!contact;

  const [formData, setFormData] = useState({
    type: 'agent' as ContactType,
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    website: '',
    linkedin_url: '',
    source: '',
    status: 'new' as ContactStatus,
    next_follow_up: '',
    notes: '',
    owner_user_id: userId,
    title: '',
    company_url: '',
    location: '',
    country: '',
    external_id: '',
    icp_fit: '' as IcpFit | '',
    segment: '' as LeadSegment | '',
    channel_preference: '' as ChannelPreference | '',
    hook: '',
    hook_source_url: '',
    hook_date: '',
    research_notes: '',
    email_subject: '',
    email_body: '',
    linkedin_note: '',
    next_step: '',
    email_source_note: '',
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [revenue, setRevenue] = useState<ContactRevenue | null>(null);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState<RehubUser[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [linking, setLinking] = useState(false);
  
  // Intelligence features state
  const [activeTab, setActiveTab] = useState<'details' | 'signals' | 'network' | 'sequences' | 'comms'>('details');
  const [signals, setSignals] = useState<Signal[]>([]);
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [loadingRelationships, setLoadingRelationships] = useState(false);
  const [clientMetrics, setClientMetrics] = useState<ClientMetrics | null>(null);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [enrolments, setEnrolments] = useState<Enrolment[]>([]);
  const [loadingEnrolments, setLoadingEnrolments] = useState(false);
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [referrerSearch, setReferrerSearch] = useState('');
  const [referrerResults, setReferrerResults] = useState<Contact[]>([]);
  const [searchingReferrers, setSearchingReferrers] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [networkSearch, setNetworkSearch] = useState('');
  const [networkResults, setNetworkResults] = useState<Contact[]>([]);
  const [searchingNetwork, setSearchingNetwork] = useState(false);
  const [copiedBooking, setCopiedBooking] = useState(false);

  // Comms (Twilio) state
  const [commsSubTab, setCommsSubTab] = useState<'messages' | 'calls'>('messages');
  const [messages, setMessages] = useState<import('../../services/twilio').Message[]>([]);
  const [calls, setCalls] = useState<import('../../services/twilio').Call[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [contactComms, setContactComms] = useState<import('../../services/twilio').ContactComms>({ whatsapp_number: null, sms_number: null });
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageChannel, setMessageChannel] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [createFollowup, setCreateFollowup] = useState(true);
  const [showCommsEditModal, setShowCommsEditModal] = useState(false);
  const [editWhatsapp, setEditWhatsapp] = useState('');
  const [editSms, setEditSms] = useState('');
  
  // Instantly integration state
  const [showInstantlyModal, setShowInstantlyModal] = useState(false);
  const [instantlyLists, setInstantlyLists] = useState<InstantlyList[]>([]);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaign[]>([]);
  const [instantlySelectedList, setInstantlySelectedList] = useState('');
  const [instantlySelectedCampaign, setInstantlySelectedCampaign] = useState('');
  const [pushingToInstantly, setPushingToInstantly] = useState(false);
  const [instantlyPushed, setInstantlyPushed] = useState(false);
  const [instantlyLastPushed, setInstantlyLastPushed] = useState<string | null>(null);
  const [loadingInstantlyOptions, setLoadingInstantlyOptions] = useState(false);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);
  const [summaryText, setSummaryText] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);

  const fetchRevenue = useCallback(async () => {
    if (!contact?.rehub_user_id) {
      setRevenue(null);
      return;
    }
    setLoadingRevenue(true);
    const data = await getContactRevenue(contact.id);
    setRevenue(data);
    setLoadingRevenue(false);
  }, [contact?.id, contact?.rehub_user_id]);

  const fetchSignals = useCallback(async () => {
    if (!contact) return;
    setLoadingSignals(true);
    const data = await getSignalsForContact(contact.id);
    setSignals(data);
    setLoadingSignals(false);
  }, [contact?.id]);

  const fetchRelationships = useCallback(async () => {
    if (!contact) return;
    setLoadingRelationships(true);
    const data = await getRelationshipsForContact(contact.id);
    setRelationships(data);
    setLoadingRelationships(false);
  }, [contact?.id]);

  const fetchEnrolments = useCallback(async () => {
    if (!contact) return;
    setLoadingEnrolments(true);
    const data = await getEnrolmentsForContact(contact.id);
    setEnrolments(data);
    setLoadingEnrolments(false);
  }, [contact?.id]);

  const fetchClientMetrics = useCallback(async () => {
    if (!contact) return;
    const data = await getClientMetrics(contact.id);
    setClientMetrics(data);
  }, [contact?.id]);

  const fetchInstantlyMapping = useCallback(async () => {
    if (!contact) return;
    const mapping = await getInstantlyMapping(contact.id);
    if (mapping?.last_pushed_at) {
      setInstantlyLastPushed(mapping.last_pushed_at);
      setInstantlyPushed(true);
    } else {
      setInstantlyLastPushed(null);
      setInstantlyPushed(false);
    }
  }, [contact?.id]);

  const fetchMessages = useCallback(async () => {
    if (!contact) return;
    setLoadingMessages(true);
    const data = await getContactMessages(contact.id);
    setMessages(data);
    setLoadingMessages(false);
  }, [contact?.id]);

  const fetchCalls = useCallback(async () => {
    if (!contact) return;
    setLoadingCalls(true);
    const data = await getContactCalls(contact.id);
    setCalls(data);
    setLoadingCalls(false);
  }, [contact?.id]);

  const fetchComms = useCallback(async () => {
    if (!contact) return;
    const data = await getContactComms(contact.id);
    setContactComms(data);
    setEditWhatsapp(data.whatsapp_number || '');
    setEditSms(data.sms_number || contact.phone || '');
    if (data.whatsapp_number) {
      setMessageChannel('whatsapp');
    } else if (data.sms_number || contact.phone) {
      setMessageChannel('sms');
    }
  }, [contact?.id, contact?.phone]);

  const fetchInstantlyOptions = useCallback(async () => {
    setLoadingInstantlyOptions(true);
    const [lists, campaigns, settings] = await Promise.all([
      getInstantlyLists(),
      getInstantlyCampaigns(),
      getInstantlyOrgSettings(team.id),
    ]);
    setInstantlyLists(lists);
    setInstantlyCampaigns(campaigns);
    setInstantlySelectedList(settings?.default_lead_list_id || '');
    setInstantlySelectedCampaign(settings?.default_campaign_id || '');
    setLoadingInstantlyOptions(false);
  }, [team.id]);

  const fetchTemplates = useCallback(async () => {
    const data = await getTaskTemplates(team.id);
    setTemplates(data);
  }, [team.id]);

  const fetchBookingUrl = useCallback(async () => {
    const settings = await getUserSettings(userId);
    setBookingUrl(settings?.booking_url || null);
  }, [userId]);

  const fetchSummary = useCallback(async () => {
    if (!contact) return;
    const data = await getLatestSummary(contact.id);
    if (data) setSummaryText(data.summary);
  }, [contact?.id]);

  useEffect(() => {
    if (contact) {
      setFormData({
        type: contact.type,
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        company: contact.company || '',
        email: contact.email || '',
        phone: contact.phone || '',
        website: contact.website || '',
        linkedin_url: contact.linkedin_url || '',
        source: contact.source || '',
        status: contact.status,
        next_follow_up: contact.next_follow_up ? contact.next_follow_up.slice(0, 16) : '',
        notes: contact.notes || '',
        owner_user_id: contact.owner_user_id || userId,
        title: contact.title || '',
        company_url: contact.company_url || '',
        location: contact.location || '',
        country: contact.country || '',
        external_id: contact.external_id || '',
        icp_fit: contact.icp_fit || '',
        segment: contact.segment || '',
        channel_preference: contact.channel_preference || '',
        hook: contact.hook || '',
        hook_source_url: contact.hook_source_url || '',
        hook_date: contact.hook_date || '',
        research_notes: contact.research_notes || '',
        email_subject: contact.email_subject || '',
        email_body: contact.email_body || '',
        linkedin_note: contact.linkedin_note || '',
        next_step: contact.next_step || '',
        email_source_note: contact.email_source_note || '',
      });
      fetchRevenue();
      fetchSignals();
      fetchRelationships();
      fetchEnrolments();
      fetchClientMetrics();
      fetchTemplates();
      fetchBookingUrl();
      fetchSummary();
      fetchInstantlyMapping();
      fetchComms();
      fetchMessages();
      fetchCalls();
    }
  }, [contact, userId, fetchRevenue, fetchSignals, fetchRelationships, fetchEnrolments, fetchClientMetrics, fetchTemplates, fetchBookingUrl, fetchSummary, fetchInstantlyMapping, fetchComms, fetchMessages, fetchCalls]);

  useEffect(() => {
    if (!linkSearch || linkSearch.length < 3) {
      setLinkResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      const results = await searchRehubUsers(linkSearch);
      setLinkResults(results);
      setSearchingUsers(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [linkSearch]);

  useEffect(() => {
    if (!referrerSearch || referrerSearch.length < 2) {
      setReferrerResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingReferrers(true);
      const results = await searchContacts(team.id, referrerSearch);
      setReferrerResults(results.filter(c => c.id !== contact?.id));
      setSearchingReferrers(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [referrerSearch, team.id, contact?.id]);

  useEffect(() => {
    if (!networkSearch || networkSearch.length < 2) {
      setNetworkResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingNetwork(true);
      const results = await searchContacts(team.id, networkSearch);
      setNetworkResults(results.filter(c => c.id !== contact?.id));
      setSearchingNetwork(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [networkSearch, team.id, contact?.id]);

  const handleLinkUser = async (rehubUser: RehubUser) => {
    if (!contact) return;
    setLinking(true);
    const success = await linkContactToRehubUser(contact.id, rehubUser.id);
    setLinking(false);
    if (success) {
      setShowLinkModal(false);
      setLinkSearch('');
      setLinkResults([]);
      onSaved();
    } else {
      setError('Failed to link account');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (looksFabricatedEmail(formData.email) && !formData.email_source_note.trim()) {
      setSaving(false);
      setError('Add an email source note for generic inboxes like info@ or hello@.');
      return;
    }

    const input: CreateContactInput = {
      team_id: team.id,
      type: formData.type,
      first_name: formData.first_name || null,
      last_name: formData.last_name || null,
      company: formData.company || null,
      email: formData.email || null,
      phone: formData.phone || null,
      website: formData.website || null,
      linkedin_url: formData.linkedin_url || null,
      source: formData.source || null,
      status: formData.status,
      next_follow_up: formData.next_follow_up ? new Date(formData.next_follow_up).toISOString() : null,
      notes: formData.notes || null,
      owner_user_id: formData.owner_user_id || null,
      full_name: [formData.first_name, formData.last_name].filter(Boolean).join(' ') || null,
      title: formData.title || null,
      company_url: formData.company_url || null,
      location: formData.location || null,
      country: formData.country || null,
      external_id: formData.external_id || null,
      icp_fit: formData.icp_fit || null,
      segment: formData.segment || null,
      channel_preference: formData.channel_preference || null,
      hook: formData.hook || null,
      hook_source_url: formData.hook_source_url || null,
      hook_date: formData.hook_date || null,
      research_notes: formData.research_notes || null,
      email_subject: formData.email_subject || null,
      email_body: formData.email_body || null,
      linkedin_note: formData.linkedin_note || null,
      next_step: formData.next_step || null,
      email_source_note: formData.email_source_note || null,
      drafted_on: formData.email_body || formData.linkedin_note ? (contact?.drafted_on || new Date().toISOString()) : contact?.drafted_on || null,
      status: formData.status === 'new' && (formData.email_body || formData.linkedin_note) ? 'drafted' : formData.status,
    };

    let result;
    if (isEdit && contact) {
      result = await updateContact({ id: contact.id, ...input });
    } else {
      result = await createContact(input);
    }

    setSaving(false);

    if (result) {
      onSaved();
      onClose();
    } else {
      setError('Failed to save contact');
    }
  };

  const handleDelete = async () => {
    if (!contact || !confirm('Are you sure you want to delete this lead?')) return;

    setDeleting(true);
    const success = await deleteContact(contact.id);
    setDeleting(false);

    if (success) {
      onSaved();
      onClose();
    } else {
      setError('Failed to delete contact');
    }
  };

  const handleSetReferrer = async (referrer: Contact) => {
    if (!contact) return;
    const success = await setReferrer(contact.id, referrer.id, referrer.type);
    if (success) {
      setShowReferrerModal(false);
      setReferrerSearch('');
      onSaved();
    }
  };

  const handleAddRelationship = async (toContact: Contact, type: string) => {
    if (!contact) return;
    const result = await createRelationship(team.id, contact.id, toContact.id, type as any);
    if (result) {
      setShowNetworkModal(false);
      setNetworkSearch('');
      fetchRelationships();
    }
  };

  const handleRemoveRelationship = async (id: string) => {
    const success = await deleteRelationship(id);
    if (success) fetchRelationships();
  };

  const handleEnrolSequence = async (templateId: string) => {
    if (!contact) return;
    const result = await enrolContactInSequence(team.id, contact.id, templateId);
    if (result) fetchEnrolments();
  };

  const handleCancelEnrolment = async (id: string) => {
    const success = await cancelEnrolment(id);
    if (success) fetchEnrolments();
  };

  const handleCopyBookingMessage = () => {
    if (!bookingUrl || !contact) return;
    const name = contact.first_name || contact.company || 'there';
    const message = generateBookingMessage(bookingUrl, name);
    navigator.clipboard.writeText(message);
    setCopiedBooking(true);
    setTimeout(() => setCopiedBooking(false), 2000);
  };

  const handleOpenInstantlyModal = async () => {
    await fetchInstantlyOptions();
    setShowInstantlyModal(true);
  };

const handlePushToInstantly = async () => {
    if (!contact || !instantlySelectedList) return;
    setPushingToInstantly(true);
    const result = await pushContactToInstantly(
      team.id,
      contact.id,
      instantlySelectedList,
      instantlySelectedCampaign || undefined
    );
    setPushingToInstantly(false);

    if (result.success) {
      setShowInstantlyModal(false);
      setInstantlyPushed(true);
      setInstantlyLastPushed(new Date().toISOString());
      onSaved();
    } else {
      setError(result.error || 'Failed to push to Instantly');
    }
  };

  const handleSendMessage = async () => {
    if (!contact || !newMessage.trim()) return;
    setSendingMessage(true);
    const result = await sendMessage(team.id, contact.id, messageChannel, newMessage.trim(), createFollowup);
    setSendingMessage(false);

    if (result.success) {
      setNewMessage('');
      fetchMessages();
      onSaved();
    } else {
      setError(result.error || 'Failed to send message');
    }
  };

  const handleSaveComms = async () => {
    if (!contact) return;
    const success = await saveContactComms(team.id, contact.id, editWhatsapp || null, editSms || null);
    if (success) {
      setShowCommsEditModal(false);
      fetchComms();
    }
  };

  const handleApplyTemplate = (template: typeof MESSAGE_TEMPLATES[0]) => {
    const name = contact?.first_name || contact?.company || 'there';
    const body = applyTemplate(template.body, { name, sender: 'Ben' });
    setNewMessage(body);
  };

  const handleSaveSummary = async () => {
    if (!contact || !summaryText.trim()) return;
    setSavingSummary(true);
    await createSummary(contact.id, summaryText.trim(), [], null, userId);
    setSavingSummary(false);
  };

  const getTemperatureIcon = (temp: string) => {
    switch (temp) {
      case 'hot': return <Flame size={14} className="text-red-500" />;
      case 'warm': return <Thermometer size={14} className="text-orange-500" />;
      default: return <Snowflake size={14} className="text-blue-400" />;
    }
  };

  const getHealthBadge = (status: string) => {
    const styles = {
      green: 'bg-green-500/20 text-green-400 border-green-500/30',
      yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      red: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return styles[status as keyof typeof styles] || styles.green;
  };

  const contactName = contact ? 
    (contact.first_name || contact.last_name ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : contact.email || 'Unknown') 
    : '';

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden animate-fadeIn flex flex-col">
        <div className="bg-slate-900 border-b border-slate-700 shrink-0">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">
                {isEdit ? contactName : 'New Lead'}
              </h2>
              {isEdit && contact && (
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${getHealthBadge(contact.health_status)}`}>
                    {contact.health_status === 'green' ? <CheckCircle2 size={12} /> : 
                     contact.health_status === 'yellow' ? <AlertCircle size={12} /> : <XCircle size={12} />}
                    {contact.health_status}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-slate-700 text-slate-200 rounded-full">
                    {getTemperatureIcon(contact.lead_temperature)}
                    {contact.lead_score} pts
                  </span>
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors">
              <X size={20} />
            </button>
          </div>
          
          {isEdit && (
            <div className="flex border-t border-slate-700/50">
              {(['details', 'signals', 'network', 'sequences', 'comms'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === tab 
                      ? 'text-orange-500 border-b-2 border-orange-500 bg-slate-800/50' 
                      : 'text-slate-400 hover:text-white border-b-2 border-transparent'
                  }`}
                >
                  {tab === 'details' && 'Details'}
                  {tab === 'signals' && `Signals (${signals.length})`}
                  {tab === 'network' && `Network (${relationships.length})`}
                  {tab === 'sequences' && 'Sequences'}
                  {tab === 'comms' && `Comms (${messages.length})`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
        {/* DETAILS TAB */}
        {(!isEdit || activeTab === 'details') && (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Lead Score & Health Summary */}
          {isEdit && contact && (
            <div className="p-4 bg-gradient-to-r from-slate-800/80 to-slate-800/50 border border-slate-700 rounded-lg">
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-xs text-slate-400 mb-1">Score</div>
                  <div className="flex items-center justify-center gap-1 text-lg font-bold text-white">
                    {getTemperatureIcon(contact.lead_temperature)}
                    {contact.lead_score}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Health</div>
                  <div className={`text-sm font-medium capitalize ${
                    contact.health_status === 'green' ? 'text-green-400' :
                    contact.health_status === 'yellow' ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {contact.health_status}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">Spend (30d)</div>
                  <div className="text-sm font-semibold text-white">
                    {clientMetrics ? formatCurrency(clientMetrics.total_spend_30d_cents, 'gbp') : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-1">All-time</div>
                  <div className="text-sm font-semibold text-emerald-400">
                    {clientMetrics ? formatCurrency(clientMetrics.total_spend_all_time_cents, 'gbp') : '—'}
                  </div>
                </div>
              </div>
              {bookingUrl && (
                <button
                  type="button"
                  onClick={handleCopyBookingMessage}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {copiedBooking ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                  {copiedBooking ? 'Copied!' : 'Copy Booking Message'}
                </button>
              )}

              {/* Instantly Push Button */}
              {contact?.email && !contact.do_not_contact && (
                <button
                  type="button"
                  onClick={handleOpenInstantlyModal}
                  disabled={pushingToInstantly}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {pushingToInstantly ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Zap size={16} />
                  )}
                  Send to Instantly
                </button>
              )}

              {instantlyPushed && instantlyLastPushed && (
                <p className="mt-2 text-xs text-slate-400 text-center">
                  Last pushed: {new Date(instantlyLastPushed).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}

              {contact?.do_not_contact && (
                <p className="mt-2 text-xs text-red-400 text-center">
                  Contact marked as do not contact
                </p>
              )}
            </div>
          )}

          {/* Referral Section */}
          {isEdit && (
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-white">Referred By</h3>
                <button
                  type="button"
                  onClick={() => setShowReferrerModal(true)}
                  className="text-xs text-orange-500 hover:text-orange-400"
                >
                  {contact?.referred_by_contact_id ? 'Change' : 'Set Referrer'}
                </button>
              </div>
              {contact?.referred_by_contact_id ? (
                <div className="text-sm text-slate-300">
                  <span className="capitalize">{contact.referral_type || 'unknown'}</span> referral
                </div>
              ) : (
                <div className="text-sm text-slate-400">No referrer set</div>
              )}
            </div>
          )}

          {/* ReHub Account Section */}
          {isEdit && (
            <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-white flex items-center gap-2">
                  <Users size={16} className="text-orange-500" />
                  ReHub Account
                </h3>
                {!contact?.rehub_user_id && (
                  <button
                    type="button"
                    onClick={() => setShowLinkModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors"
                  >
                    <Link2 size={14} />
                    Link Account
                  </button>
                )}
              </div>
              
              {contact?.rehub_user_id ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <div className="w-2 h-2 bg-green-400 rounded-full" />
                    Linked to ReHub
                  </div>
                  {loadingRevenue ? (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 size={14} className="animate-spin" />
                      Loading revenue...
                    </div>
                  ) : revenue ? (
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      <div className="p-2.5 bg-slate-900 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">Total Spend</div>
                        <div className="text-sm font-semibold text-white">
                          {formatCurrency(revenue.total_spend_cents, 'gbp')}
                        </div>
                      </div>
                      <div className="p-2.5 bg-slate-900 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">Credits</div>
                        <div className="text-sm font-semibold text-white">
                          {revenue.current_credits ?? 0}
                        </div>
                      </div>
                      <div className="p-2.5 bg-slate-900 rounded-lg">
                        <div className="text-xs text-slate-400 mb-1">Commission</div>
                        <div className="text-sm font-semibold text-emerald-400">
                          {formatCurrency(revenue.total_commission_cents, 'gbp')}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-400">No purchase history</div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-slate-400">
                  Not linked to a ReHub account yet
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Company</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {formData.email && (
                  <a
                    href={`mailto:${formData.email}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors shrink-0"
                    title="Send email"
                  >
                    <Mail size={18} />
                  </a>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone</label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {formData.phone && (
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('initiate-call', { detail: { number: formData.phone } }))}
                    className="p-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors shrink-0"
                    title="Call contact"
                  >
                    <Phone size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Website</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://"
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {formData.website && (
                  <a
                    href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors shrink-0"
                    title="Open website"
                  >
                    <Globe size={18} />
                  </a>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">LinkedIn</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {formData.linkedin_url && (
                  <a
                    href={formData.linkedin_url.startsWith('http') ? formData.linkedin_url : `https://${formData.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg transition-colors shrink-0"
                    title="Open LinkedIn"
                  >
                    <Linkedin size={18} />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as ContactType })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {CONTACT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select source</option>
                {CONTACT_SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as ContactStatus })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {CONTACT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Owner</label>
              <select
                value={formData.owner_user_id}
                onChange={(e) => setFormData({ ...formData, owner_user_id: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Unassigned</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.email || m.user_id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Next Follow-up</label>
            <input
              type="datetime-local"
              value={formData.next_follow_up}
              onChange={(e) => setFormData({ ...formData, next_follow_up: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
          </div>

          <div className="pt-4 border-t border-slate-700">
            <h3 className="text-sm font-semibold text-white mb-3">Outbound research & drafts</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Title</label>
                <input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Prospect ID</label>
                <input value={formData.external_id} onChange={(e) => setFormData({ ...formData, external_id: e.target.value })} placeholder="P045" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Segment</label>
                <select value={formData.segment} onChange={(e) => setFormData({ ...formData, segment: e.target.value as LeadSegment | '' })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="">—</option>
                  {LEAD_SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">ICP fit</label>
                <select value={formData.icp_fit} onChange={(e) => setFormData({ ...formData, icp_fit: e.target.value as IcpFit | '' })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="">—</option>
                  {ICP_FITS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Channel</label>
                <select value={formData.channel_preference} onChange={(e) => setFormData({ ...formData, channel_preference: e.target.value as ChannelPreference | '' })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="">—</option>
                  {CHANNEL_PREFERENCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Location</label>
                <input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Country</label>
                <input value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Hook</label>
              <textarea value={formData.hook} onChange={(e) => setFormData({ ...formData, hook: e.target.value })} rows={2} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
              {formData.hook && !formData.hook_source_url && (
                <p className="text-xs text-amber-400 mt-1">Add a source URL for this hook.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Hook source URL</label>
                <input value={formData.hook_source_url} onChange={(e) => setFormData({ ...formData, hook_source_url: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Hook date</label>
                <input type="date" value={formData.hook_date} onChange={(e) => setFormData({ ...formData, hook_date: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Research notes</label>
              <textarea value={formData.research_notes} onChange={(e) => setFormData({ ...formData, research_notes: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email subject</label>
              <input value={formData.email_subject} onChange={(e) => setFormData({ ...formData, email_subject: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email body</label>
              <textarea value={formData.email_body} onChange={(e) => setFormData({ ...formData, email_body: e.target.value })} rows={6} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
              <p className="text-xs text-slate-500 mt-1">{ctaHintForSegment(formData.segment)}</p>
              {looksFabricatedEmail(formData.email) && !formData.email_source_note && (
                <p className="text-xs text-amber-400 mt-1">Generic inbox like info@ needs a source note before saving.</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Email source note</label>
              <input value={formData.email_source_note} onChange={(e) => setFormData({ ...formData, email_source_note: e.target.value })} placeholder="Where this email came from" className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">LinkedIn connect note <span className="text-slate-500">(soft 250 chars)</span></label>
              <textarea value={formData.linkedin_note} onChange={(e) => setFormData({ ...formData, linkedin_note: e.target.value })} rows={3} maxLength={300} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none" />
              <p className="text-xs text-slate-500 mt-1">{formData.linkedin_note.length}/250 · Copy connect note only — never auto-send.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Next step</label>
              <input value={formData.next_step} onChange={(e) => setFormData({ ...formData, next_step: e.target.value })} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-4 border-t border-slate-700">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Lead'}
            </button>
          </div>
        </form>
        )}

        {/* SIGNALS TAB */}
        {isEdit && activeTab === 'signals' && (
          <div className="p-6 space-y-4">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Activity size={16} className="text-orange-500" />
              Product Signals
            </h3>
            {loadingSignals ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : signals.length > 0 ? (
              <div className="space-y-2">
                {signals.map((signal) => (
                  <div key={signal.id} className="p-3 bg-slate-800 border border-slate-700 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm font-medium text-white">{signal.title}</div>
                        <div className="text-xs text-slate-400 mt-1">
                          {signal.type} • {new Date(signal.created_at).toLocaleDateString('en-GB')}
                        </div>
                      </div>
                      <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
                        {signal.type.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                No signals recorded yet
              </div>
            )}

            {/* Summary Section */}
            <div className="pt-4 border-t border-slate-700">
              <h3 className="text-sm font-medium text-white mb-2">Lead Summary</h3>
              <textarea
                value={summaryText}
                onChange={(e) => setSummaryText(e.target.value)}
                placeholder="Add notes about this lead's business needs, recommended actions..."
                rows={4}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
              />
              <button
                type="button"
                onClick={handleSaveSummary}
                disabled={savingSummary || !summaryText.trim()}
                className="mt-2 px-4 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 text-white text-sm rounded-lg transition-colors"
              >
                {savingSummary ? 'Saving...' : 'Save Summary'}
              </button>
            </div>
          </div>
        )}

        {/* NETWORK TAB */}
        {isEdit && activeTab === 'network' && (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                <Network size={16} className="text-orange-500" />
                Relationships
              </h3>
              <button
                type="button"
                onClick={() => setShowNetworkModal(true)}
                className="text-xs text-orange-500 hover:text-orange-400"
              >
                + Add Connection
              </button>
            </div>
            {loadingRelationships ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : relationships.length > 0 ? (
              <div className="space-y-2">
                {relationships.map((rel) => {
                  const isFrom = rel.from_contact_id === contact?.id;
                  const linkedContact = isFrom ? rel.to_contact : rel.from_contact;
                  const linkedName = linkedContact ? 
                    `${linkedContact.first_name || ''} ${linkedContact.last_name || ''}`.trim() || linkedContact.email 
                    : 'Unknown';
                  return (
                    <div key={rel.id} className="p-3 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-white">{linkedName}</div>
                        <div className="text-xs text-slate-400">
                          {rel.type.replace(/_/g, ' ')} • {(linkedContact as any)?.company || '—'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveRelationship(rel.id)}
                        className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                No relationships mapped yet
              </div>
            )}
          </div>
        )}

        {/* SEQUENCES TAB */}
        {isEdit && activeTab === 'sequences' && (
          <div className="p-6 space-y-4">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Play size={16} className="text-orange-500" />
              Task Sequences
            </h3>

            {/* Active Enrolments */}
            {enrolments.filter(e => e.status === 'active').length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Active</div>
                {enrolments.filter(e => e.status === 'active').map((enrol) => (
                  <div key={enrol.id} className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-white">{enrol.template?.name || 'Sequence'}</div>
                      <div className="text-xs text-slate-400">
                        Started {new Date(enrol.started_at).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleCancelEnrolment(enrol.id)}
                      className="px-2 py-1 text-xs text-red-400 hover:text-red-300 border border-red-500/30 rounded transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Available Templates */}
            <div className="space-y-2">
              <div className="text-xs text-slate-400 uppercase tracking-wide">Enrol in Sequence</div>
              {templates.length > 0 ? (
                templates.map((template) => {
                  const isEnrolled = enrolments.some(e => e.template_id === template.id && e.status === 'active');
                  return (
                    <div key={template.id} className="p-3 bg-slate-800 border border-slate-700 rounded-lg flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-white">{template.name}</div>
                        <div className="text-xs text-slate-400">
                          {template.steps.length} steps
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleEnrolSequence(template.id)}
                        disabled={isEnrolled}
                        className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white rounded transition-colors"
                      >
                        {isEnrolled ? 'Enrolled' : 'Start'}
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="py-4 text-center text-slate-400 text-sm">
                  No sequences available. Create one in Settings.
                </div>
              )}
            </div>
          </div>
        )}

        {/* COMMS TAB */}
        {isEdit && activeTab === 'comms' && (
          <div className="flex flex-col h-full">
            {/* Sub-tabs */}
            <div className="flex border-b border-slate-700">
              <button
                onClick={() => setCommsSubTab('messages')}
                className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  commsSubTab === 'messages'
                    ? 'text-orange-500 border-b-2 border-orange-500'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <MessageSquare size={16} />
                Messages ({messages.length})
              </button>
              <button
                onClick={() => setCommsSubTab('calls')}
                className={`flex-1 px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  commsSubTab === 'calls'
                    ? 'text-orange-500 border-b-2 border-orange-500'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Phone size={16} />
                Calls ({calls.length})
              </button>
            </div>

            {/* Contact Numbers */}
            <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-700 flex items-center justify-between">
              <div className="text-sm text-slate-400">
                {contactComms.whatsapp_number && (
                  <span className="mr-3">
                    <span className="text-green-400">WA:</span> {contactComms.whatsapp_number}
                  </span>
                )}
                {(contactComms.sms_number || contact?.phone) && (
                  <span>
                    <span className="text-blue-400">SMS:</span> {contactComms.sms_number || contact?.phone}
                  </span>
                )}
                {!contactComms.whatsapp_number && !contactComms.sms_number && !contact?.phone && (
                  <span className="text-yellow-400">No phone numbers configured</span>
                )}
              </div>
              <button
                onClick={() => setShowCommsEditModal(true)}
                className="p-1.5 text-slate-400 hover:text-white transition-colors"
              >
                <Edit2 size={14} />
              </button>
            </div>

            {/* Messages Sub-tab */}
            {commsSubTab === 'messages' && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Message List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="py-8 text-center text-slate-400 text-sm">
                      No messages yet. Start the conversation below.
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 rounded-lg ${
                            msg.direction === 'outbound'
                              ? 'bg-orange-600 text-white'
                              : 'bg-slate-700 text-white'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                          <div className="flex items-center justify-between gap-2 mt-1.5">
                            <span className={`text-xs ${
                              msg.direction === 'outbound' ? 'text-orange-200' : 'text-slate-400'
                            }`}>
                              {msg.channel.toUpperCase()}
                            </span>
                            <span className={`text-xs ${
                              msg.direction === 'outbound' ? 'text-orange-200' : 'text-slate-400'
                            }`}>
                              {new Date(msg.sent_at).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Templates */}
                <div className="px-4 py-2 border-t border-slate-700">
                  <div className="flex items-center gap-2 overflow-x-auto pb-2">
                    <span className="text-xs text-slate-500 shrink-0">Templates:</span>
                    {MESSAGE_TEMPLATES.map((tpl) => (
                      <button
                        key={tpl.name}
                        onClick={() => handleApplyTemplate(tpl)}
                        className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors shrink-0"
                      >
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-slate-700">
                  <div className="flex items-center gap-2 mb-2">
                    <select
                      value={messageChannel}
                      onChange={(e) => setMessageChannel(e.target.value as 'whatsapp' | 'sms')}
                      className="px-2 py-1.5 text-sm bg-slate-800 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {contactComms.whatsapp_number && <option value="whatsapp">WhatsApp</option>}
                      {(contactComms.sms_number || contact?.phone) && <option value="sms">SMS</option>}
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-slate-400">
                      <input
                        type="checkbox"
                        checked={createFollowup}
                        onChange={(e) => setCreateFollowup(e.target.checked)}
                        className="rounded bg-slate-800 border-slate-600"
                      />
                      Create follow-up
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      rows={2}
                      className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage || (!contactComms.whatsapp_number && !contactComms.sms_number && !contact?.phone)}
                      className="px-4 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center"
                    >
                      {sendingMessage ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Calls Sub-tab */}
            {commsSubTab === 'calls' && (
              <div className="flex-1 overflow-y-auto p-4">
                {loadingCalls ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                  </div>
                ) : calls.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 text-sm">
                    No calls yet. Use the dialler to make a call.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {calls.map((call) => (
                      <div
                        key={call.id}
                        className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          {call.direction === 'outbound' ? (
                            <PhoneOutgoing size={18} className="text-blue-400" />
                          ) : (
                            <PhoneIncoming size={18} className="text-green-400" />
                          )}
                          <div>
                            <div className="text-sm text-white capitalize">
                              {call.direction} call
                            </div>
                            <div className="text-xs text-slate-400">
                              {call.started_at && new Date(call.started_at).toLocaleString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-xs px-2 py-0.5 rounded ${
                            call.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            call.status === 'no-answer' ? 'bg-red-500/20 text-red-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {call.status}
                          </div>
                          {call.duration_seconds && (
                            <div className="text-xs text-slate-400 mt-1">
                              {Math.floor(call.duration_seconds / 60)}:{(call.duration_seconds % 60).toString().padStart(2, '0')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Click to Call Button */}
                {(contactComms.sms_number || contact?.phone) && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <button
                      onClick={() => {
                        // This will be handled by the Dialler component
                        const event = new CustomEvent('initiate-call', {
                          detail: { number: contactComms.sms_number || contact?.phone }
                        });
                        window.dispatchEvent(event);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors"
                    >
                      <PhoneCall size={18} />
                      Call {contactComms.sms_number || contact?.phone}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>{/* End scrollable content area */}
      </div>
    </div>

    {/* Comms Edit Modal */}
    {showCommsEditModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowCommsEditModal(false)} />
        <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Phone size={18} className="text-orange-500" />
              Contact Numbers
            </h3>
            <button onClick={() => setShowCommsEditModal(false)} className="p-1.5 text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                WhatsApp Number
              </label>
              <input
                type="tel"
                value={editWhatsapp}
                onChange={(e) => setEditWhatsapp(e.target.value)}
                placeholder="+44 7700 900000"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-slate-500 mt-1">Include country code (e.g., +44)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                SMS Number
              </label>
              <input
                type="tel"
                value={editSms}
                onChange={(e) => setEditSms(e.target.value)}
                placeholder="+44 7700 900000"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveComms}
                className="flex-1 px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors"
              >
                Save Numbers
              </button>
              <button
                onClick={() => setShowCommsEditModal(false)}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Referrer Modal */}
    {showReferrerModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowReferrerModal(false)} />
        <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Set Referrer</h3>
            <button onClick={() => setShowReferrerModal(false)} className="p-1.5 text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={referrerSearch}
              onChange={(e) => setReferrerSearch(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {searchingReferrers ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 size={20} className="animate-spin mr-2" />
              </div>
            ) : referrerResults.length > 0 ? (
              referrerResults.map((ref) => (
                <button
                  key={ref.id}
                  type="button"
                  onClick={() => handleSetReferrer(ref)}
                  className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-700 rounded-lg text-left transition-colors"
                >
                  <div>
                    <div className="text-sm font-medium text-white">
                      {ref.first_name || ref.last_name ? `${ref.first_name || ''} ${ref.last_name || ''}`.trim() : ref.email}
                    </div>
                    <div className="text-xs text-slate-400">{ref.company || ref.type}</div>
                  </div>
                  <span className="text-xs text-slate-400 capitalize">{ref.type}</span>
                </button>
              ))
            ) : referrerSearch.length >= 2 ? (
              <div className="py-8 text-center text-slate-400 text-sm">No contacts found</div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">Enter at least 2 characters</div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Network Modal */}
    {showNetworkModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowNetworkModal(false)} />
        <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Add Connection</h3>
            <button onClick={() => setShowNetworkModal(false)} className="p-1.5 text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={networkSearch}
              onChange={(e) => setNetworkSearch(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {searchingNetwork ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 size={20} className="animate-spin mr-2" />
              </div>
            ) : networkResults.length > 0 ? (
              networkResults.map((nc) => (
                <div key={nc.id} className="p-3 bg-slate-800 rounded-lg">
                  <div className="text-sm font-medium text-white mb-2">
                    {nc.first_name || nc.last_name ? `${nc.first_name || ''} ${nc.last_name || ''}`.trim() : nc.email}
                  </div>
                  <div className="flex gap-2">
                    {contact?.type === 'photographer' && nc.type === 'agent' && (
                      <button
                        type="button"
                        onClick={() => handleAddRelationship(nc, 'photographer_serves_agent')}
                        className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded"
                      >
                        Serves Agent
                      </button>
                    )}
                    {contact?.type === 'agent' && nc.type === 'photographer' && (
                      <button
                        type="button"
                        onClick={() => handleAddRelationship(nc, 'agent_refers_photographer')}
                        className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded"
                      >
                        Refers Photographer
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleAddRelationship(nc, 'partner')}
                      className="px-2 py-1 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded"
                    >
                      Partner
                    </button>
                  </div>
                </div>
              ))
            ) : networkSearch.length >= 2 ? (
              <div className="py-8 text-center text-slate-400 text-sm">No contacts found</div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">Enter at least 2 characters</div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Link Account Modal */}
    {showLinkModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowLinkModal(false)} />
        <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Link2 size={18} className="text-orange-500" />
              Link ReHub Account
            </h3>
            <button
              onClick={() => setShowLinkModal(false)}
              className="p-1.5 text-slate-400 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
          
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              placeholder="Search by email (min 3 chars)..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="max-h-64 overflow-y-auto space-y-2">
            {searchingUsers ? (
              <div className="flex items-center justify-center py-8 text-slate-400">
                <Loader2 size={20} className="animate-spin mr-2" />
                Searching...
              </div>
            ) : linkResults.length > 0 ? (
              linkResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleLinkUser(user)}
                  disabled={linking}
                  className="w-full flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-700 rounded-lg text-left transition-colors disabled:opacity-50"
                >
                  <div>
                    <div className="text-sm font-medium text-white">{user.email}</div>
                    <div className="text-xs text-slate-400">
                      {user.credits ?? 0} credits
                    </div>
                  </div>
                  {linking ? (
                    <Loader2 size={16} className="animate-spin text-orange-500" />
                  ) : (
                    <UserPlus size={16} className="text-orange-500" />
                  )}
                </button>
              ))
            ) : linkSearch.length >= 3 ? (
              <div className="py-8 text-center text-slate-400 text-sm">
                No ReHub users found
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                Enter at least 3 characters to search
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Instantly Push Modal */}
    {showInstantlyModal && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/70" onClick={() => setShowInstantlyModal(false)} />
        <div className="relative w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Zap size={18} className="text-purple-500" />
              Send to Instantly
            </h3>
            <button
              onClick={() => setShowInstantlyModal(false)}
              className="p-1.5 text-slate-400 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {loadingInstantlyOptions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Lead List *
                </label>
                <select
                  value={instantlySelectedList}
                  onChange={(e) => setInstantlySelectedList(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a lead list...</option>
                  {instantlyLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Campaign (optional)
                </label>
                <select
                  value={instantlySelectedCampaign}
                  onChange={(e) => setInstantlySelectedCampaign(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">No campaign</option>
                  {instantlyCampaigns.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </option>
                  ))}
                </select>
              </div>

              {instantlyLists.length === 0 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-400">
                  No lead lists found. Configure your Instantly API key and create lists in Instantly first.
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={handlePushToInstantly}
                  disabled={!instantlySelectedList || pushingToInstantly}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors"
                >
                  {pushingToInstantly ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Zap size={16} />
                  )}
                  Push to Instantly
                </button>
                <button
                  onClick={() => setShowInstantlyModal(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default LeadDrawer;
